import { Col, Form, Input, Row, Select, Modal } from "antd";
import { useForm } from "antd/lib/form/Form";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntTheNeedsOfThePopulation } from "../../../../models/organic/theNeedsOfThePopulation";
import { DraggableModal } from "../../../../components/DraggableModal";
import { SelectProductType } from "../../../../components/selects";
import SelectTerritorialUnitsCode from "../../../../components/selects/SelectTerritorialUnitsCode";
import { MapAddSingleMarker } from "../../../../components/maps/MapAddPoint";
import { GeoJsonPoint } from "../../../../models/geoJsonPoint";
import { Geometry } from "../../../../models/geoJson";

enum FormField {
  id,
  year,
  regionId,
  areaId,
  territorialUnitId,
  productTypeId,
  latitude,
  longitude,
  averageConsumption,
  population
}

interface IProps {
  show: boolean;
  item: EntTheNeedsOfThePopulation;
  onHide: () => void;
  onSubmit: (pos: EntTheNeedsOfThePopulation) => Promise<boolean>;
}

export const AddTheNeedsOfThePopulationModal = (props: IProps) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [geoJsonData, setGeoJsonData] = useState<GeoJsonPoint|null>(null);

  const [selectedRegionId, setSelectedRegionId] = useState<number|null>(null);
  const [selectedRegionPolygonJson, setSelectedRegionPolygonJson] = useState<string|null>(null);
  const [selectedRegionCode, setSelectedRegionCode] = useState<string|null>(null);

  const [selectedAreaCode, setSelectedAreaCode] = useState<string|null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<number|null>(null);

  useEffect(() => {
	if (props.show) setFieldsFromEntity();
	setSelectedRegionId(props.item.region?.id || null);
	setSelectedAreaId(props.item.area?.id || null);
	setSelectedAreaCode(props.item.area?.code || null);
  }, [props.show]);

  const setFieldsFromEntity = (): void => {
	form.setFields([
	  { name: FormField.year, value: props.item.year },
	  { name: FormField.regionId, value: props.item.region?.id },
	  { name: FormField.areaId, value: props.item.area?.id },
	  { name: FormField.territorialUnitId, value: props.item.territorialUnit?.id },
	  { name: FormField.productTypeId, value: props.item.productType?.id },
	  { name: FormField.averageConsumption, value: props.item.averageConsumption },
	  { name: FormField.population, value: props.item.population },
	]);
  };

  const updateGeoJsonPoint = () => {
	const latitude = props.item.latitude;
	const longitude = props.item.longitude;

	if (latitude !== undefined && latitude !== null && longitude !== undefined && longitude !== null) {
	  const geoJsonPoint = new GeoJsonPoint();
	  geoJsonPoint.geometry = new Geometry();
	  geoJsonPoint.geometry.coordinates = [longitude, latitude];
	  setGeoJsonData(geoJsonPoint);
	} else {
	  console.error(t("breeding_farms.error_geometry"));
	}
  };

  useEffect(() => {
	if (props.item.latitude && props.item.longitude) {
	  updateGeoJsonPoint();
	} else {
	  setGeoJsonData(null);
	}
  }, [props.item.latitude, props.item.longitude]);

  const setEntityFromFields = (): EntTheNeedsOfThePopulation => {
	let res = _.cloneDeep(props.item);
	res.year = form.getFieldValue(FormField.year);
	res.regionId = form.getFieldValue(FormField.regionId);
	res.areaId = form.getFieldValue(FormField.areaId);
	res.territorialUnitId = form.getFieldValue(FormField.territorialUnitId);
	res.productTypeId = form.getFieldValue(FormField.productTypeId);
	res.averageConsumption = form.getFieldValue(FormField.averageConsumption);
	res.population = form.getFieldValue(FormField.population);
	if (geoJsonData !== null) {
	  res.latitude = geoJsonData.geometry?.coordinates[1] as number;
	  res.longitude = geoJsonData.geometry?.coordinates[0] as number;
	}
	return res;
  };

  const hideModal = () => {
	props.onHide();
	setSelectedRegionId(null);
	setSelectedRegionPolygonJson(null);
	setSelectedAreaCode(null);
	setGeoJsonData(null);
  };

  const handleSubmit = async () => {
	setLoading(true);
	try {
	  await form.validateFields();
	  if (geoJsonData !== null) {
		const data = setEntityFromFields();
		const result = await props.onSubmit(data);
		if (result) hideModal();
	  } else {
		Modal.error({ title: t("msg.error"), content: t("breeding_farms.choose_location") });
	  }
	} finally {
	  setLoading(false);
	}
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, index) => currentYear - index);

  return (
	<DraggableModal
	  title={ props.item.id === null ? t("general.add") : t("general.edit") }
	  visible={ props.show }
	  onCancel={ hideModal }
	  onSubmit={ handleSubmit }
	  submitLoading={ loading }
	  width={ 1000 }
	>
	  <Form initialValues={ { remember: true } } autoComplete="off" layout="vertical" form={ form }>
		<Row style={ { margin: 0 } } gutter={ 16 }>
		  <Col className="gutter-row" span={ 12 }>
			<Form.Item
			  label={ t('year') }
			  name={ FormField.year }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Select
				placeholder={ t("msg.selectPlaceholder") }
				allowClear
			  >
				{ years.map((year) => (
				  <Select.Option key={ year } value={ year }>
					{ year }
				  </Select.Option>
				)) }
			  </Select>
			</Form.Item>
			<Form.Item
			  label={ t('titles.territorial_unit_area') }
			  name={ FormField.regionId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <SelectTerritorialUnitsCode
				parentId={ 1 }
				onChange={ (value, option: any) => {
				  setSelectedRegionId(Number(value));
				  setSelectedRegionPolygonJson(option?.data?.polygonJson);
				  setSelectedRegionCode(option?.data?.code);
				  setSelectedAreaCode(null);
				  setSelectedAreaId(null);
				  form.setFieldsValue({
					[FormField.regionId]: value,
					[FormField.areaId]: null,
					[FormField.territorialUnitId]: null,
				  });
				} }
			  />
			</Form.Item>
			{ selectedRegionId || props.item.region ? (
			  <Form.Item
				label={ t('titles.territorialUnitDistricts') }
				name={ FormField.areaId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectTerritorialUnitsCode
				  type={ ['AREA', 'CITY_OF_REPUBLICAN_SIGNIFICANCE'] }
				  parentId={ selectedRegionId }
				  onChange={ (value, option: any) => {
					setSelectedRegionPolygonJson(option?.data?.polygonJson);
					setSelectedAreaCode(option?.data?.code || null);
					setSelectedAreaId(value as number);
					form.setFields([{ name: FormField.territorialUnitId, value: null }]);
				  } }
				/>
			  </Form.Item>
			) : null }
			{ selectedAreaCode || props.item.territorialUnit ? (
			  <Form.Item
				label={ t("titles.territorialUnitVillage") }
				name={ FormField.territorialUnitId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectTerritorialUnitsCode areaCode={ selectedAreaCode }/>
			  </Form.Item>
			) : null }
		  </Col>
		  <Col className="gutter-row" span={ 12 }>
			<Form.Item
			  label={ t('Тип продукта') }
			  name={ FormField.productTypeId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <SelectProductType/>
			</Form.Item>
			<Form.Item
			  label={ t('Население') }
			  name={ FormField.population }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Input type="number" placeholder="150000"/>
			</Form.Item>
			<Form.Item
			  label={ t('Среднее потребление') }
			  name={ FormField.averageConsumption }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Input type="number" step="0.1" placeholder="90.5"/>
			</Form.Item>
		  </Col>
		</Row>
		<MapAddSingleMarker 
		  polygonJson={ selectedRegionPolygonJson } 
		  setGeoJsonData={ setGeoJsonData }
		  geoJsonData={ geoJsonData }
		/>
	  </Form>
	</DraggableModal>
  );
};
